import { useOutletContext } from "react-router-dom";
import { Calendar, Trophy, Users, Star, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kickoff-central-10/artifacts/72zh29ie_Middel%2020%40300x.png";
const COURT_IMAGE = "https://images.pexels.com/photos/7648071/pexels-photo-7648071.jpeg";

const timeline = [
  {
    year: "2016",
    title: "Oprichting",
    description: "Futsal Gent werd opgericht door een groep gepassioneerde futsalliefhebbers in de stad Gent. Met slechts één seniorenteam begon het avontuur.",
    highlight: true
  },
  {
    year: "2017",
    title: "Eerste Seizoen",
    description: "Ons eerste competitieseizoen in de provinciale reeksen. Ondanks een moeilijke start eindigden we in de middenmoot.",
    highlight: false
  },
  {
    year: "2018",
    title: "Eerste Promotie",
    description: "Na een sterk seizoen promoveerden we naar de tweede nationale afdeling. De basis voor verdere groei was gelegd.",
    highlight: true
  },
  {
    year: "2019",
    title: "Uitbreiding Jeugd",
    description: "Start van onze jeugdopleiding met U13 en U15 teams. De eerste stap naar een volwaardige clubstructuur.",
    highlight: false
  },
  {
    year: "2020",
    title: "Volledige Jeugdacademie",
    description: "Lancering van ons complete jeugdprogramma met U13, U15, en U17 teams. Ook Jong FTG werd opgericht als doorstroomteam.",
    highlight: true
  },
  {
    year: "2021",
    title: "Consolidatie",
    description: "Ondanks de COVID-pandemie bleef de club groeien. Nieuwe partnerships en sponsorovereenkomsten versterkten onze basis.",
    highlight: false
  },
  {
    year: "2022",
    title: "Promotie naar Eerste Nationale",
    description: "Historische promotie naar de Belgian Futsal League, de hoogste afdeling van het Belgische futsal!",
    highlight: true
  },
  {
    year: "2023",
    title: "Eerste Seizoen op Topniveau",
    description: "Een leerjaar in de hoogste klasse. We eindigden op een respectabele 6e plaats en bouwden waardevolle ervaring op.",
    highlight: false
  },
  {
    year: "2024",
    title: "Ambitie voor de Toekomst",
    description: "Met versterkte selectie en verbeterde faciliteiten mikken we op de top 3 en Europees voetbal.",
    highlight: true
  }
];

const achievements = [
  { icon: Trophy, value: "3", label: "Kampioenstitels" },
  { icon: Users, value: "6", label: "Actieve Teams" },
  { icon: Calendar, value: "8", label: "Jaren Bestaan" },
  { icon: Star, value: "150+", label: "Leden" }
];

export default function Geschiedenis() {
  const context = useOutletContext();
  const isDark = context?.isDark ?? true;

  return (
    <div className="min-h-screen" data-testid="geschiedenis-page">
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[300px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${COURT_IMAGE})` }}
        />
        <div className="hero-gradient absolute inset-0" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end pb-12">
          <div className="flex items-center gap-6">
            <img src={LOGO_URL} alt="Futsal Gent" className="h-20 md:h-28 w-auto" />
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white font-['Chivo']">ONZE GESCHIEDENIS</h1>
              <p className="text-[hsl(48,96%,53%)] font-medium mt-2">Van droom naar realiteit</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 -mt-20 mb-12 relative z-10">
          {achievements.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className={`text-center ${isDark ? 'bg-slate-900 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
                <CardContent className="pt-6">
                  <Icon className={`h-8 w-8 mx-auto mb-2 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`} />
                  <p className={`text-3xl font-black font-['Chivo'] ${isDark ? 'text-white' : 'text-slate-900'}`}>{stat.value}</p>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{stat.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Introduction */}
        <Card className={`mb-12 ${isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200 shadow-md'}`}>
          <CardContent className="py-8">
            <p className={`text-lg leading-relaxed text-center max-w-3xl mx-auto ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Wat begon als een droom van een handvol futsalenthousiastelingen is uitgegroeid tot 
              een van de meest ambitieuze futsalclubs in België. Dit is ons verhaal - een reis 
              van passie, doorzettingsvermogen en groei.
            </p>
          </CardContent>
        </Card>

        {/* Timeline */}
        <h2 className={`text-3xl font-black font-['Chivo'] mb-8 text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Onze Tijdlijn
        </h2>
        
        <div className="relative">
          {/* Timeline line */}
          <div className={`absolute left-1/2 transform -translate-x-1/2 h-full w-1 ${isDark ? 'bg-[hsl(217,91%,60%)]/30' : 'bg-blue-200'}`} />
          
          {timeline.map((item, index) => (
            <div key={item.year} className={`relative flex items-center mb-8 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
              {/* Content */}
              <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                <Card className={`${item.highlight ? (isDark ? 'bg-gradient-to-br from-blue-900/50 to-slate-900/50 border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-white border-blue-200') : (isDark ? 'bg-slate-900/50 border-white/10' : 'bg-white border-slate-200')} shadow-md`}>
                  <CardContent className="py-6">
                    <span className={`text-sm font-bold ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'}`}>{item.year}</span>
                    <h3 className={`text-xl font-bold mt-1 mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>{item.title}</h3>
                    <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{item.description}</p>
                  </CardContent>
                </Card>
              </div>
              
              {/* Center dot */}
              <div className="absolute left-1/2 transform -translate-x-1/2 z-10">
                <div className={`w-4 h-4 rounded-full ${item.highlight ? 'bg-[hsl(48,96%,53%)]' : (isDark ? 'bg-[hsl(217,91%,60%)]' : 'bg-blue-500')}`} />
              </div>
              
              {/* Empty space */}
              <div className="w-5/12" />
            </div>
          ))}
        </div>

        {/* Future */}
        <Card className={`mt-12 ${isDark ? 'bg-gradient-to-r from-slate-900 to-blue-900/50 border-white/10' : 'bg-gradient-to-r from-slate-50 to-blue-50 border-slate-200 shadow-md'}`}>
          <CardContent className="py-12 text-center">
            <h3 className={`text-2xl font-black font-['Chivo'] mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              De Toekomst
            </h3>
            <p className={`max-w-2xl mx-auto mb-6 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
              Onze reis is nog lang niet voorbij. Met ambitieuze plannen voor uitbreiding, 
              nieuwe faciliteiten en Europese deelname kijken we vol vertrouwen naar de toekomst.
            </p>
            <div className={`inline-flex items-center gap-2 ${isDark ? 'text-[hsl(48,96%,53%)]' : 'text-amber-500'} font-medium`}>
              <span>Samen schrijven we geschiedenis</span>
              <ArrowRight className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
